import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { IBackgroundFillInfo } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import {
    ChartManualLayout,
    ShapeProps,
    TextProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/style.interface';
import { ITextEditor } from '../../property/text/interface/text-editor.interface';
import {
    SelectedElement,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { IElementBaseModel } from '../base/element-base-model.interface';

// v2的类型删除了，这里兼容一下
type SubChartModel = any;

export enum TrendlineType {
    LINEAR = 'linear',  // 线性
    EXPONENTIAL = 'exp',    // 指数
    LOG = 'log',    // 对数
    POWER = 'power',    // 乘幂
    POLYNOMIAL = 'poly',    // 多项式
    MOVING_AVG = 'movingAvg' // 移动平均
}

export interface IChartModel extends IElementBaseModel {
    title?: ChartTitle;
    plotArea: ChartPlotArea;
    legend?: ChartLegend;
    style?: number;
    selectedElement: SelectedElement | null;
    editorInstance: ITextEditor | null;
    selectionStyles: any;
    isActualRenderChart: boolean;
    // getAxes(): IChartAxes;
    // getChartTitle(): IChartTitleModule;
    // getChartLegend(): IChartLegendModule;
    // getDataLabel(seriesId, dataPointId?): IChartDataLabelModule<IBaseDataLabel> | undefined;
    // getGeometry(seriesId, dataPointId?, showMarker?);
    // getTrendLine(seriesId, trendlineIndex);
    // getHighLowLines(chartId)
    // getUpDownBars(chartId, barType)
    // updateDataLabels(value): void;
    // updateTrendline(seriesIndex: number, value: boolean | TrendlineType): void;
    // getSelectedElementModule(showMarker?: boolean): IChartBaseModule | null;
}

type PickEnum<T, K extends T> = {
    [P in keyof K]: P extends K ? P : never;
};

// 图表子表类型，从UIModelType中获取子集
export type ChartType = PickEnum<UIModelType,
| UIModelType.BAR
| UIModelType.BAR_3D
| UIModelType.LINE
| UIModelType.LINE_3D
| UIModelType.PIE
| UIModelType.PIE_3D
| UIModelType.RADAR
// 圆环图
| UIModelType.DOUGHNUT
// 散点图
| UIModelType.SCATTER
// 面积图
| UIModelType.AREA
| UIModelType.AREA_3D
// 气泡图
| UIModelType.BUBBLE
// 股价图
| UIModelType.STOCK
// 子母图
| UIModelType.OF_PIE
>;

export const ChartType = {
    BAR: UIModelType.BAR as ChartType,
    BAR_3D: UIModelType.BAR_3D as ChartType,
    LINE: UIModelType.LINE as ChartType,
    LINE_3D: UIModelType.LINE_3D as ChartType,
    PIE: UIModelType.PIE as ChartType,
    PIE_3D: UIModelType.PIE_3D as ChartType,
    RADAR: UIModelType.RADAR as ChartType,
    // 圆环图
    DOUGHNUT: UIModelType.DOUGHNUT as ChartType,
    // 散点图
    SCATTER: UIModelType.SCATTER as ChartType,
    // 面积图
    AREA: UIModelType.AREA as ChartType,
    AREA_3D: UIModelType.AREA_3D as ChartType,
    // 气泡图
    BUBBLE: UIModelType.BUBBLE as ChartType,
    // 股价图
    STOCK: UIModelType.STOCK as ChartType,
    // 子母图
    OF_PIE: UIModelType.OF_PIE as ChartType,
};

export interface IChartShapeProps {
    style: {
        fillBackground?: IBackgroundFillInfo
        [key: string]: any;
    }
    [key: string]: any;
}

export interface IChartMarker {
    symbol: EMarkerSymbol;
    size?: number;
    props: any;
}

export enum EMarkerSymbol {
    NONE = 'none',
    SQUARE = 'square',
    DIAMOND = 'diamond',
    TRIANGLE = 'triangle',
    X = 'x',
    STAR = 'star',
    DOT = 'dot',
    DASH = 'dash',
    CIRCLE = 'circle',
    PLUS = 'plus',
    PICTURE = 'picture',
}


export interface ChartLegend extends ShapeProps, TextProps {
    position?: Position;
    legendEntryList?: Array<LegendEntry>;
    overlay?: boolean;
    layout?: ChartManualLayout;
}

export interface LegendEntry extends TextProps {
    id: number;
    delete: boolean;
}

export interface ChartPlotArea extends ShapeProps {
    categoryAxes?: IAxisProps[];
    dateAxes?: IAxisProps[];
    valueAxes?: IAxisProps[];
    charts: Array<SubChartModel>;
    layout?: ChartManualLayout;
}

export interface ChartTitle extends ShapeProps, TextProps {
    overlay?: boolean;
    text?: TextProps;
    layout?: ChartManualLayout;
}

export enum ETickMark {
    NONE = 'none',
    CROSS = 'cross',
    IN = 'in',
    OUT = 'out'
}

export enum EUnit {
    HUNDREDS = 'hundreds',
    THOUSANDS = 'thousands',
    TEN_THOUSANDS = 'tenThousands',
    HUNDRED_THOUSANDS = 'hundredThousands',
    MILLIONS = 'millions',
    TEN_MILLIONS = 'tenMillions',
    HUNDRED_MILLIONS = 'hundredMillions',
    BILLIONS = 'billions',
    TRILLIONS = 'trillions'
}

export enum ETickLabelPosition {
    HIGH = 'high',
    LOW = 'low',
    NEXT_TO = 'nextTo',
    NONE = 'none'
}

export enum ECrossBetween {
    BETWEEN = 'between',
    MID_CAT = 'midCat'
}

export enum EAxisPosition {
    Left = 'l',
    Right = 'r',
    Top = 't',
    Bottom = 'b'
}

export enum EGrouping {
    CLUSTERED = 'clustered',
    STACKED = 'stacked',
    PERCENT_STACKED = 'percentStacked',
    STANDARD = 'standard'
}

export enum Position {
    Bottom = 'b',
    Right = 'r',
    Top = 't',
    TopRight = 'tr',
    Left = 'l'
}

export interface ChartAxisUnitsLabel extends ShapeProps, TextProps {
    text?: TextProps;
    layout: ChartManualLayout;
}

export interface ChartAxisUnits {
    unit: EUnit;
    label?: ChartAxisUnitsLabel;
}

export interface IAxisProps extends ShapeProps, TextProps {
    axId: number;
    type?: 'value' | 'date' | 'category';
    index?: number;
    position: Position;
    title: ChartTitle;
    scaling: ChartAxisScaling;
    // 坐标轴主刻度线单位，对应office中的坐标轴选项-单位-大
    majorUnit?: number;
    // 坐标轴次刻度线单位，对应office中的坐标轴选项-单位-小
    minorUnit?: number;
    // 坐标轴数据格式
    numFmt?: {
        formatCode: string;
    };
    majorTickMark: ETickMark;
    minorTickMark: ETickMark;
    tickLabelPosition: ETickLabelPosition;
    crossBetween?: ECrossBetween;
    crossAx: number;
    crosses?: string;
    lblOffset?: number;
    majorGridlines?: IChartGridLine;
    minorGridlines?: IChartGridLine;
    units: ChartAxisUnits;
    isDelete: boolean;
    autoLabeled: boolean;
    lblAlgn: string;
}

export interface ChartAxisScaling {
    // 坐标轴的排列方向，minMax: 从小到大 maxMin: 从大到小
    orientation: 'minMax' | 'maxMin';
    // 坐标轴最大值
    max?: number;
    // 坐标轴最小值
    min?: number;
    // 对数底数
    logBase?: number;
}
// 目前只有shapeProps

export type IChartGridLine = ShapeProps;

/**
 * 坐标轴标题内容
 */
export interface IAxesTitle {
    primaryTitleX?: string;
    primaryTitleY?: string;
    secondTitleX?: string;
    secondTitleY?: string;
}

export const OTHER_DPT_ID = 'otherDPt';
